import axios from "axios";
import type { MessageInstance } from "antd/es/message/interface";
import { logger } from "@/helpers/tslog";
import { getToken } from "@/utils/localstorage";
import {
  getCurrentUserEmail,
  getPresentationDialogReturnUrl,
  isOneDriveAuthenticated,
  startOneDriveAuthRedirect,
} from "@/utils/presentationOneDriveAuth";
import type {
  SlideFrameId,
  SlidesDialogOutput,
} from "@/state/slidesDialogStore";

type CurrentUserResponse = {
  id: string;
  email: string;
  fullName: string;
  conversationId: string | null;
};

export interface SlidesGeneratePayload {
  primaryTicker: string;
  primaryExchange: string;
  primaryCompanyName: string;
  slidesDialogOutput: SlidesDialogOutput;
  selectedSlideFrameIds: SlideFrameId[];
  formatTemplateId: string;
  contentReference?: string;
}

export interface SlidesGenerateResult {
  jobId: string;
  estimatedDurationMinute: number;
  friendlyRequestIdLine: string;
}

function apiBase(): string | null {
  const u = process.env.API_URL;
  return u ? u.replace(/\/+$/, "") : null;
}

function localTimingFields(): {
  workflowClientLocalDate: string;
  workflowLocalTimeFolder: string;
  userTimeZone?: string;
} {
  const now = new Date();
  const y = now.getFullYear();
  const mo = String(now.getMonth() + 1).padStart(2, "0");
  const da = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  let userTimeZone: string | undefined;
  try {
    userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    userTimeZone = undefined;
  }
  return {
    workflowClientLocalDate: `${y}-${mo}-${da}`,
    workflowLocalTimeFolder: `${hh}-${mm}-${ss}`,
    userTimeZone,
  };
}

export async function ensureSlidesOneDriveReady(
  message: MessageInstance,
  setBusy: (v: boolean) => void
): Promise<boolean> {
  const apiUrl = apiBase();
  if (!apiUrl) {
    message.warning("OneDrive check skipped (API_URL not set).");
    return true;
  }
  try {
    setBusy(true);
    const userEmail = await getCurrentUserEmail();
    if (!userEmail) {
      message.error("Could not load your account email. Sign in and try again.");
      return false;
    }
    const authed = await isOneDriveAuthenticated(userEmail);
    if (authed) return true;

    const returnUrl = getPresentationDialogReturnUrl();
    if (!returnUrl) {
      message.error("Could not build return URL for OneDrive sign-in.");
      return false;
    }
    message.info("Redirecting to Microsoft to connect OneDrive…");
    startOneDriveAuthRedirect(userEmail, returnUrl);
    return false;
  } catch (e) {
    logger.error("[slidesGenerateFlow] OneDrive auth failed", e);
    message.error("OneDrive authentication failed.");
    return false;
  } finally {
    setBusy(false);
  }
}

export async function triggerSlidesGeneration(
  payload: SlidesGeneratePayload,
  message: MessageInstance,
  setBusy: (v: boolean) => void
): Promise<SlidesGenerateResult | null> {
  const apiUrl = apiBase();
  if (!apiUrl) {
    message.error("Slides generation requires API_URL.");
    return null;
  }
  try {
    setBusy(true);
    const token = await getToken();
    const timing = localTimingFields();
    const body = {
      flow: "slides" as const,
      primaryTicker: payload.primaryTicker,
      primaryExchange: payload.primaryExchange,
      primaryCompanyName: payload.primaryCompanyName,
      slidesDialogOutput: payload.slidesDialogOutput,
      selectedSlideFrameIds: payload.selectedSlideFrameIds,
      formatTemplateId: payload.formatTemplateId,
      contentReference: payload.contentReference,
      ...timing,
    };
    const response = await axios.post<{
      jobId: string;
      estimatedDurationSec?: number;
      estimatedDurationMinute?: number;
      friendlyRequestIdLine?: string;
    }>(`${apiUrl}/slide-generation/generate`, body, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    });
    return {
      jobId: response.data.jobId,
      estimatedDurationMinute: response.data.estimatedDurationMinute ?? 10,
      friendlyRequestIdLine: response.data.friendlyRequestIdLine ?? response.data.jobId,
    };
  } catch (e) {
    logger.error("[slidesGenerateFlow] Failed to trigger slides generation:", e);
    message.error("Failed to trigger slides generation");
    return null;
  } finally {
    setBusy(false);
  }
}

export async function sendSlidesTeamsMessage(
  jobId: string,
  estimatedDurationMinute: number,
  friendlyRequestIdLine: string,
  message: MessageInstance
): Promise<boolean> {
  const apiUrl = apiBase();
  if (!apiUrl) {
    message.warning("Teams notification skipped (API not configured).");
    return true;
  }
  try {
    const token = await getToken();
    const { data: user } = await axios.get<CurrentUserResponse>(`${apiUrl}/user/current-user`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    });
    if (!user?.conversationId) {
      message.error("Install the Teams app and link your account to send notifications.");
      return false;
    }
    const userName = user.fullName?.trim().split(/\s+/)[0] || "User";
    const minuteLabel = estimatedDurationMinute === 1 ? "1 minute" : `${estimatedDurationMinute} minutes`;
    const requestIdReference = (friendlyRequestIdLine && friendlyRequestIdLine.trim()) || jobId;
    const messageText = `Hey ${userName} - Thank you for the workflow request — Request ID: ${requestIdReference}. I'm working on it now and estimate it will take about ${minuteLabel}. I'll let you know as soon as it's ready or if I experience any delays.`;
    logger.info("[slidesGenerateFlow] Sending message to Teams:", messageText);
    await axios.post(
      `${apiUrl}/teams/send-to-bot`,
      { message: messageText, conversationId: user.conversationId },
      { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true }
    );
    return true;
  } catch (e) {
    logger.error("[slidesGenerateFlow] Failed to send Teams message:", e);
    message.error("Failed to send message to Teams. Please try again.");
    return false;
  }
}
